import comp from '../components/barrel';
import svg from '../js/svg';

const EP = 'application/app-list';
const PAGE_SIZE = 50;
const VAR_PAGE = 'willsApps_selected_page';
const COL_COUNT = 13;

const HEADERS = [
  'Active',
  'Testament',
  'App type',
  'Offshore will',
  'Status',
  'Will number',
  'Will verified',
  'Rec back date',
  'Signed date',
  'Party 1',
  'Party 2',
  'Created',
  'Updated',
];

function cellText(val) {
  return val != null && String(val).trim() !== '' ? String(val) : '';
}

function ynText(val) {
  if (val === true || val === 'true' || val === 1) return 'Yes';
  if (val === false || val === 'false' || val === 0) return 'No';
  return '—';
}

async function dateText(val) {
  if (val == null || String(val).trim() === '') return '—';
  return await U.formatDate(val);
}

async function dateTimeText(val) {
  if (val == null || String(val).trim() === '') return '—';
  return await U.formatDateTime(val);
}

function advisorUuid() {
  const fromVars =
    (thsPage && thsPage.vars && thsPage.vars.advisor_uuid) ||
    (thsTab && thsTab.vars && thsTab.vars.advisor_uuid);
  const fromDetails = thsPage && thsPage.advisorDetails && thsPage.advisorDetails.advisor_uuid;
  const raw = fromVars != null && String(fromVars).trim() !== '' ? fromVars : fromDetails;
  return raw ? String(raw).trim() : '';
}

export default {
  async prepare() {
    if (thsPage && thsPage.vars && (thsPage.vars[VAR_PAGE] == null || thsPage.vars[VAR_PAGE] < 1)) {
      thsPage.vars[VAR_PAGE] = 1;
    }
    const vars = await this.buildRequestVars(thsPage && thsPage.vars ? thsPage.vars[VAR_PAGE] : 1);
    if (!vars) return;
    this.recipe.methods.push(await O.mPOST(EP, vars));
  },

  async render() {
    const rec = this.recipe.methods;
    let data = {};
    if (rec.length) {
      data = await O.PREP(rec);
    }
    await this.loadPage(data);
  },

  async buildRequestVars(page) {
    const advisor = advisorUuid();
    if (!advisor) return null;
    const hdrs = await O.getAuthHdrs();
    return {
      filters: {
        search_filters: '',
        will_filter: '',
        status: '',
        advisor,
        openApplicationsYN: false,
      },
      limit: PAGE_SIZE,
      page: page || 1,
      user_uuid: hdrs.user_uuid || '',
    };
  },

  async loadPage(data) {
    this.buildTableHead();
    const listRes = data[EP] || {};
    if (!advisorUuid()) {
      await this.buildTableBody([], 'No advisor selected. Open an advisor from the list.');
      await this.buildPaginationButtons({total_count: 0, total_pages: 0});
      return;
    }
    if (listRes.status && String(listRes.status).toUpperCase() === 'FAILED') {
      const err = (listRes.data && listRes.data.error) || 'Unable to load wills and applications';
      await this.buildTableBody([], err);
      await this.buildPaginationButtons({total_count: 0, total_pages: 0});
      return;
    }
    const rows = Array.isArray(listRes.data) ? listRes.data : [];
    const paginationState = listRes.pagination || {
      total_count: rows.length,
      total_records: rows.length,
      page_size: PAGE_SIZE,
      current_page: 1,
      total_pages: rows.length ? 1 : 0,
    };
    if (thsPage && thsPage.vars) {
      const current = Number(paginationState.current_page) || thsPage.vars[VAR_PAGE] || 1;
      thsPage.vars[VAR_PAGE] = current;
      thsPage.vars.selected_page = current;
    }
    await this.buildTableBody(rows);
    await this.buildPaginationButtons(paginationState);
  },

  buildTableHead() {
    const tableHead = U.id('advisorWillsTableHead');
    if (!tableHead) return;
    tableHead.innerHTML = '';
    const headRow = tableHead.insertRow();
    HEADERS.forEach((label, i) => {
      const cell = headRow.insertCell(i);
      cell.innerHTML = label;
    });
  },

  async buildTableBody(listData, emptyMessage) {
    const tbody = U.id('advisorWillsTableBody');
    if (!tbody) return;
    tbody.innerHTML = '';

    if (!listData.length) {
      const tr = tbody.insertRow();
      const cell = tr.insertCell(0);
      cell.colSpan = COL_COUNT;
      cell.textContent = emptyMessage || 'No wills or applications found.';
      return;
    }

    for (const row of listData) {
      const tr = tbody.insertRow();
      const isActive = row.active_yn === true || row.active_yn === 'true' || row.active_yn === 1;
      if (!isActive) tr.classList.add('advisor-will-inactive-row');
      const activeCell = tr.insertCell(0);
      activeCell.className = 'advisor-icon-cell';
      const icon = isActive ? svg.tick() : svg.cross();
      icon.classList.add(isActive ? 'text-green-600' : 'text-red-600');
      icon.setAttribute('title', isActive ? 'Active' : 'Inactive');
      activeCell.appendChild(icon);
      tr.insertCell(1).textContent = cellText(row.testament) || '—';
      tr.insertCell(2).textContent = cellText(row.app_type) || '—';
      tr.insertCell(3).textContent = ynText(row.offshore_will);
      const statusCell = tr.insertCell(4);
      if (cellText(row.status)) {
        statusCell.appendChild(await comp.statusTag(row.status));
      } else {
        statusCell.textContent = '—';
      }
      tr.insertCell(5).textContent = cellText(row.importwillnumber) || '—';
      tr.insertCell(6).textContent = cellText(row.importwillverified)
        ? await dateText(row.importwillverified)
        : ynText(row.importwillverified);
      tr.insertCell(7).textContent = await dateText(row.importrecbackdate);
      tr.insertCell(8).textContent = await dateText(row.importsigneddate);
      tr.insertCell(9).textContent = cellText(row.party1_names) || '—';
      tr.insertCell(10).textContent = cellText(row.party2_names) || '—';
      tr.insertCell(11).textContent = await dateText(row.created_date);
      tr.insertCell(12).textContent = await dateTimeText(row.updated_date);
    }
  },

  async buildPaginationButtons(paginationState) {
    const totalPages = Number(paginationState.total_pages) || 0;
    const currentPage = Number(paginationState.current_page) || 1;
    const wrapper = U.id('advisorWillsPaginationWrapper');
    const container = U.id('advisorWillsPaginationContainer');
    if (container) U.unhide('advisorWillsPaginationContainer');

    if (thsPage && thsPage.vars) {
      thsPage.vars[VAR_PAGE] = currentPage;
      thsPage.vars.selected_page = currentPage;
    }

    if (wrapper && totalPages > 0) {
      await comp.paginationButtons({
        totalPages,
        wrapperID: 'advisorWillsPaginationWrapper',
      });
      const pageButtons = wrapper.querySelectorAll('.pageButton');
      pageButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          const v = Number(e.currentTarget.getAttribute('value'));
          if (!Number.isFinite(v) || v < 1) return;
          if (thsPage && thsPage.vars) {
            thsPage.vars[VAR_PAGE] = v;
            thsPage.vars.selected_page = v;
          }
          this.getWillsList();
        });
      });
    } else if (wrapper) {
      wrapper.innerHTML = '';
    }

    const totalRecords =
      paginationState.total_records != null
        ? paginationState.total_records
        : paginationState.total_count != null
          ? paginationState.total_count
          : '-';
    const pages = paginationState.total_pages != null ? paginationState.total_pages : '-';
    const totalRecordsEl = U.id('advisorWillsTotalRecordsDisplay');
    const totalPagesEl = U.id('advisorWillsTotalPagesDisplay');
    if (totalRecordsEl) totalRecordsEl.textContent = totalRecords;
    if (totalPagesEl) totalPagesEl.textContent = pages;
  },

  async getWillsList() {
    const page = (thsPage && thsPage.vars && parseInt(thsPage.vars[VAR_PAGE], 10)) || 1;
    const vars = await this.buildRequestVars(page);
    if (!vars) return;
    const res = await O.PREP([await O.mPOST(EP, vars)]);
    await this.loadPage(res);
  },

  async downloadWillsExcel() {
    const vars = await this.buildRequestVars(1);
    if (!vars) {
      comp.toast('No advisor selected');
      return;
    }
    const body = {
      filters: vars.filters,
      user_uuid: vars.user_uuid,
      export_file: 'All',
    };
    try {
      const hdrs = await O.getAuthHdrs();
      const methodObj = await O.mPOST(EP, body);
      const response = await fetch(methodObj.endPoint + methodObj.method, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hdrs.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        comp.toast('Unable to download the Excel file');
        return;
      }
      let fileName = 'wills_applications_export_all';
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
        const plainMatch = contentDisposition.match(/filename\s*=\s*([^;\s]+)/i);
        if (quotedMatch && quotedMatch[1]) fileName = quotedMatch[1].trim();
        else if (plainMatch && plainMatch[1]) fileName = plainMatch[1].trim();
      }
      if (!fileName.toLowerCase().endsWith('.xlsx') && !fileName.toLowerCase().endsWith('.xls')) {
        fileName += '.xlsx';
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      comp.toast('Unable to download the Excel file');
    }
  },
};
